/**
 * Tiny REST client for the Build page. Hand-rolled rather than dragging
 * in axios/swr — three endpoints, all GETs except `/builds`.
 *
 * The base URL is wired by Vite at build time from `VITE_API_URL`. Production
 * builds get it from `.env.production` (and the deploy:frontend task env):
 * `https://api.offsider.dev.peoplease.ai`. Locally it falls back to
 * `http://localhost:8000` so `pnpm dev:spa` works against
 * `uv run uvicorn offsider.api:app`.
 */

import type {
  BusinessProfile,
  BuildResponse,
  EvalRow,
  KbItemStatus,
  KbResponse,
  KbScoreRow,
} from "./types.js";

/**
 * Domain-specific error thrown by `patchKbItem`. Carries the HTTP status
 * so the UI layer can distinguish 429 (toast "rate limited") from 500
 * (toast "something went wrong") without re-parsing the message.
 */
export class KbEditError extends Error {
  status: number;
  detail: string;
  constructor(status: number, detail: string) {
    super(detail);
    this.name = "KbEditError";
    this.status = status;
    this.detail = detail;
  }
}

/**
 * Thrown by `postBuild` on 429. Carries the `Retry-After` header
 * (parsed to a number of seconds) so the Enter form can surface a
 * human-readable hint + the activation-CTA copy from ADR-0029.
 */
export class BuildCapError extends Error {
  retryAfterSeconds: number;
  detail: string;
  constructor(retryAfterSeconds: number, detail: string) {
    super(detail);
    this.name = "BuildCapError";
    this.retryAfterSeconds = retryAfterSeconds;
    this.detail = detail;
  }
}

/** Build-time API base URL (Vite env). */
export const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

/**
 * POST /builds. Returns the build's job id + slug + `was_new` flag.
 *
 * ADR-0029: `email` is required; the API upserts on
 * `(LOWER(email), apex_domain(source))` so the same pair always returns
 * the same slug, and every POST kicks a fresh re-extract.
 *
 * 429 → throws `BuildCapError` carrying the `Retry-After` seconds so
 * the form can surface the activation CTA + human-readable retry hint.
 * Other non-2xx responses throw a plain `Error` so the caller renders a
 * generic "something went wrong" inline.
 */
export async function postBuild(body: {
  source: string;
  email: string;
}): Promise<BuildResponse> {
  const res = await fetch(`${API_URL}/builds`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (res.status === 429) {
    const retryHeader = res.headers.get("retry-after");
    const retryAfterSeconds = retryHeader ? Number(retryHeader) : 0;
    let detail = "Daily refresh cap reached. Sign up to keep going.";
    try {
      const payload = (await res.json()) as { detail?: string };
      if (payload.detail) detail = payload.detail;
    } catch {
      /* body wasn't JSON — keep the default message */
    }
    throw new BuildCapError(
      Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : 0,
      detail,
    );
  }
  if (!res.ok) {
    throw new Error(`build failed: HTTP ${res.status}`);
  }
  return (await res.json()) as BuildResponse;
}

/**
 * GET /kb/{slug}. Returns the build row + (when populated) the typed
 * BusinessProfile. 404 → null so the page can render a "not found" state.
 */
export async function getKb(slug: string): Promise<KbResponse | null> {
  const res = await fetch(`${API_URL}/kb/${encodeURIComponent(slug)}`);
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`kb fetch failed: HTTP ${res.status}`);
  }
  return (await res.json()) as KbResponse;
}

/**
 * GET /eval/{slug}. Returns the eval row when the post-build battery has
 * been queued; 404 while the build is still pre-ready or the row hasn't
 * been written yet. The Pipeline component polls this to swap a hardcoded
 * "Passed X of Y" chip for the actual battery result.
 */
export async function getEval(slug: string): Promise<EvalRow | null> {
  const res = await fetch(`${API_URL}/eval/${encodeURIComponent(slug)}`);
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`eval fetch failed: HTTP ${res.status}`);
  }
  return (await res.json()) as EvalRow;
}

/**
 * GET /score/{slug}. Returns the deterministic coverage score row when the
 * Build pipeline has finished writing the profile. 404 while still
 * building. The Pipeline component reads this to surface a real
 * "{groups} groups · {items} answers" metric on the synthesize stage.
 */
export async function getScore(slug: string): Promise<KbScoreRow | null> {
  const res = await fetch(`${API_URL}/score/${encodeURIComponent(slug)}`);
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`score fetch failed: HTTP ${res.status}`);
  }
  return (await res.json()) as KbScoreRow;
}

/**
 * PATCH /kb/{slug}/items. Edits a single KbItem on the profile and
 * returns the refreshed `BusinessProfile`. Throws a `KbEditError`
 * carrying the HTTP status + detail on non-2xx so the UI can surface
 * 404 (item moved), 429 (rate limited), and 500 (server error) with
 * different toasts.
 */
export async function patchKbItem(
  slug: string,
  body: {
    group_name: string;
    item_key: string;
    value: string;
    status?: KbItemStatus;
  },
): Promise<BusinessProfile> {
  const res = await fetch(`${API_URL}/kb/${encodeURIComponent(slug)}/items`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ status: "confirmed", ...body }),
  });
  return throwOrParse(res);
}

/**
 * POST /kb/{slug}/items. Appends a new KbItem to an existing group.
 * Same error shape as `patchKbItem` so callers can share the toast
 * pattern. 409 specifically means "key already exists in that group"
 * — distinct from 404 which means the group itself is missing.
 */
export async function postKbItem(
  slug: string,
  body: {
    group_name: string;
    item_key: string;
    value: string;
    status?: KbItemStatus;
  },
): Promise<BusinessProfile> {
  const res = await fetch(`${API_URL}/kb/${encodeURIComponent(slug)}/items`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ status: "confirmed", ...body }),
  });
  return throwOrParse(res);
}

/**
 * POST /kb/{slug}/groups. Creates a new section with one or more
 * initial items. 409 means a group with that name already exists.
 */
export async function postKbGroup(
  slug: string,
  body: {
    name: string;
    icon: string;
    items: { key: string; value: string; status?: KbItemStatus }[];
  },
): Promise<BusinessProfile> {
  const res = await fetch(`${API_URL}/kb/${encodeURIComponent(slug)}/groups`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  return throwOrParse(res);
}

async function throwOrParse(res: Response): Promise<BusinessProfile> {
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const errPayload = (await res.json()) as { detail?: string };
      if (errPayload.detail) detail = errPayload.detail;
    } catch {
      /* body wasn't JSON — keep the status-only message */
    }
    throw new KbEditError(res.status, detail);
  }
  return (await res.json()) as BusinessProfile;
}
